import { z } from 'zod';

// src/spam/thresholds.ts

// src/spam/spam-thresholds.generated.json
var spam_thresholds_generated_default = {
  campaign: {
    min_recipients: 15,
    min_sends: 20
  },
  dust_usd: 0.01,
  fallback_family: "evm",
  native_dust_max_by_chain: {},
  native_dust_max_by_coin: {
    BTC: {
      amount: "1000",
      decimals: 8
    }
  },
  native_dust_max_by_family: {
    evm: "500000000000000",
    hypercore: "1000",
    solana: "500000",
    tron: "1000000",
    utxo: "1000"
  },
  native_dust_settled_max: {
    amount: "10000000000000",
    decimals: 18
  },
  native_proof_min_by_family: {
    evm: "1000000000000000",
    hypercore: "10000",
    solana: "1000000",
    tron: "10000000",
    utxo: "10000"
  },
  token_dust_raw_max: "1000"
};

// src/spam/thresholds.ts
var RawAmount = z.string().regex(/^\d+$/, "a raw base-unit amount is a decimal string").transform((value) => BigInt(value));
var StatedAmount = z.object({
  amount: RawAmount,
  decimals: z.number().int().nonnegative()
});
var SpamThresholdsSchema = z.object({
  dust_usd: z.number().positive(),
  fallback_family: z.string().min(1),
  native_dust_max_by_family: z.record(z.string(), RawAmount),
  native_dust_settled_max: StatedAmount,
  native_dust_max_by_coin: z.record(z.string(), StatedAmount),
  native_dust_max_by_chain: z.record(z.string(), RawAmount),
  token_dust_raw_max: RawAmount,
  native_proof_min_by_family: z.record(z.string(), RawAmount),
  campaign: z.object({
    min_sends: z.number().int().positive(),
    min_recipients: z.number().int().positive()
  })
}).refine((t) => t.native_proof_min_by_family[t.fallback_family] !== void 0, {
  message: "the fallback family needs a native proof floor"
});
var SPAM_THRESHOLDS = Object.freeze(SpamThresholdsSchema.parse(spam_thresholds_generated_default));
var SPAM_THRESHOLDS_SNAPSHOT = Object.freeze(spam_thresholds_generated_default);
var DUST_USD_FLOOR = SPAM_THRESHOLDS.dust_usd;
var CAMPAIGN_MIN_SENDS = SPAM_THRESHOLDS.campaign.min_sends;
var CAMPAIGN_MIN_RECIPIENTS = SPAM_THRESHOLDS.campaign.min_recipients;
var SPAM_FALLBACK_FAMILY = SPAM_THRESHOLDS.fallback_family;
var NATIVE_PROOF_MIN_BY_FAMILY = Object.freeze({
  ...SPAM_THRESHOLDS.native_proof_min_by_family
});
var FALLBACK_NATIVE_PROOF_MIN = NATIVE_PROOF_MIN_BY_FAMILY[SPAM_FALLBACK_FAMILY];
var NATIVE_DUST_MAX_BY_FAMILY = Object.freeze({
  ...SPAM_THRESHOLDS.native_dust_max_by_family
});
function nativeProofMinForFamily(family) {
  const key = family || SPAM_FALLBACK_FAMILY;
  return NATIVE_PROOF_MIN_BY_FAMILY[key] ?? null;
}
var SPAM_THRESHOLDS_KEY = "spam_thresholds";
var BOUNDS = {
  dust_usd: [1e-4, 100],
  token_dust_raw_max: [0n, 10n ** 30n],
  campaign_min_sends: [2n, 100000n],
  campaign_min_recipients: [2n, 100000n]
};
var SPAM_THRESHOLD_SCALARS = Object.freeze(
  Object.keys(BOUNDS)
);
var SPAM_THRESHOLD_BOUNDS = Object.freeze(
  Object.fromEntries(
    SPAM_THRESHOLD_SCALARS.map((name) => [
      name,
      Object.freeze({ low: Number(BOUNDS[name][0]), high: Number(BOUNDS[name][1]) })
    ])
  )
);
var SPAM_THRESHOLD_FAMILY_TABLES = ["native_dust_max_by_family"];
var SPAM_THRESHOLD_PLATFORM_FAMILY_TABLES = ["native_proof_min_by_family"];
var SPAM_THRESHOLD_TUNABLES = Object.freeze(
  [...SPAM_THRESHOLD_SCALARS, ...SPAM_THRESHOLD_FAMILY_TABLES].sort()
);
var MAX_RAW = 2n ** 256n - 1n;
function isRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function whole(raw) {
  if (typeof raw === "bigint") return raw;
  if (typeof raw === "number") return Number.isInteger(raw) ? BigInt(raw) : null;
  if (typeof raw === "string") {
    const text = raw.trim();
    return /^\d+$/.test(text) ? BigInt(text) : null;
  }
  return null;
}
var FLOAT_TEXT = /^[+-]?(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?$/i;
var NON_FINITE_TEXT = /^[+-]?(?:inf|infinity|nan)$/i;
function pyFloat(raw) {
  if (typeof raw === "number") return raw;
  if (typeof raw === "bigint") return Number(raw);
  if (typeof raw !== "string") return void 0;
  const text = raw.trim();
  if (NON_FINITE_TEXT.test(text)) {
    if (/nan/i.test(text)) return Number.NaN;
    return text.startsWith("-") ? Number.NEGATIVE_INFINITY : Number.POSITIVE_INFINITY;
  }
  return FLOAT_TEXT.test(text) ? Number(text) : void 0;
}
function pyG(n) {
  if (n === 0) return "0";
  if (!Number.isFinite(n)) return String(n);
  const [mantissa = "", exponent = "0"] = n.toExponential(5).split("e");
  const x = Number(exponent);
  const strip = (text) => text.includes(".") ? text.replace(/0+$/, "").replace(/\.$/, "") : text;
  if (x < -4 || x >= 6) return `${strip(mantissa)}e${x < 0 ? "-" : "+"}${String(Math.abs(x)).padStart(2, "0")}`;
  return strip(n.toFixed(Math.max(0, 5 - x)));
}
function scalarOverride(name, raw) {
  const { low, high } = SPAM_THRESHOLD_BOUNDS[name];
  const outside = `${name} must be between ${pyG(low)} and ${pyG(high)}`;
  if (name === "dust_usd") {
    const value2 = typeof raw === "boolean" ? void 0 : pyFloat(raw);
    if (value2 === void 0) return { problem: `${name} must be a number` };
    if (!Number.isFinite(value2)) return { problem: `${name} must be a finite number` };
    return low <= value2 && value2 <= high ? { value: value2 } : { problem: outside };
  }
  const value = whole(raw);
  if (value === null) return { problem: `${name} must be a whole number` };
  const [wholeLow, wholeHigh] = BOUNDS[name];
  return wholeLow <= value && value <= wholeHigh ? { value } : { problem: outside };
}
function spamThresholdsWithOverrides(thresholds, overrides) {
  if (!isRecord(overrides) || Object.keys(overrides).length === 0) return thresholds;
  const next = {
    ...thresholds,
    campaign: { ...thresholds.campaign },
    native_dust_max_by_family: { ...thresholds.native_dust_max_by_family }
  };
  let changed = false;
  for (const name of SPAM_THRESHOLD_SCALARS) {
    if (!Object.hasOwn(overrides, name)) continue;
    const parsed = scalarOverride(name, overrides[name]);
    if (parsed.problem !== void 0) continue;
    changed = true;
    if (name === "dust_usd") next.dust_usd = Number(parsed.value);
    else if (name === "token_dust_raw_max") next.token_dust_raw_max = BigInt(parsed.value);
    else if (name === "campaign_min_sends") next.campaign.min_sends = Number(parsed.value);
    else next.campaign.min_recipients = Number(parsed.value);
  }
  for (const table of SPAM_THRESHOLD_FAMILY_TABLES) {
    const raw = overrides[table];
    if (!isRecord(raw)) continue;
    const target = next[table];
    for (const [family, amount] of Object.entries(raw)) {
      const value = whole(amount);
      if (!Object.hasOwn(target, family) || value === null || value <= 0n || value > MAX_RAW) continue;
      target[family] = value;
      changed = true;
    }
  }
  return changed ? Object.freeze(next) : thresholds;
}
var pyStr = (text) => `'${text}'`;
var pyList = (items) => `[${items.map(pyStr).join(", ")}]`;
var pyFloatRepr = (value) => Number.isInteger(value) ? value.toFixed(1) : String(value);
function validateSpamThresholds(thresholds) {
  const problems = [];
  const positive = (name, value) => {
    const ok = typeof value === "bigint" ? value > 0n : Number.isInteger(value) && value > 0;
    if (!ok) problems.push(`${name} must be a positive integer; got ${String(value)}`);
  };
  const usd = thresholds.dust_usd;
  if (typeof usd !== "number" || !Number.isFinite(usd) || usd <= 0) {
    problems.push(`dust_usd must be a positive finite number; got ${pyFloatRepr(Number(usd))}`);
  }
  const ceilings = thresholds.native_dust_max_by_family;
  const floors = thresholds.native_proof_min_by_family;
  for (const [family, ceiling] of Object.entries(ceilings)) positive(`native_dust_max_by_family[${pyStr(family)}]`, ceiling);
  for (const [family, floor] of Object.entries(floors)) positive(`native_proof_min_by_family[${pyStr(family)}]`, floor);
  const settled = thresholds.native_dust_settled_max.amount;
  positive("native_dust_settled_wei_max", settled);
  for (const [coin, entry] of Object.entries(thresholds.native_dust_max_by_coin)) {
    positive(`native_dust_max_by_coin[${pyStr(coin)}] ceiling`, entry.amount);
  }
  for (const [chain, ceiling] of Object.entries(thresholds.native_dust_max_by_chain)) {
    positive(`native_dust_max_by_chain[${pyStr(chain)}]`, ceiling);
  }
  positive("token_dust_raw_max", thresholds.token_dust_raw_max);
  positive("campaign_min_sends", thresholds.campaign.min_sends);
  positive("campaign_min_recipients", thresholds.campaign.min_recipients);
  const ceilingFamilies = new Set(Object.keys(ceilings));
  const floorFamilies = new Set(Object.keys(floors));
  const onlyCeilings = [...ceilingFamilies].filter((f) => !floorFamilies.has(f)).sort();
  const onlyFloors = [...floorFamilies].filter((f) => !ceilingFamilies.has(f)).sort();
  if (onlyCeilings.length || onlyFloors.length) {
    problems.push(
      `native_dust_max_by_family and native_proof_min_by_family must name the same families; only ceilings: ${pyList(onlyCeilings)}, only floors: ${pyList(onlyFloors)}`
    );
  }
  const fallback = thresholds.fallback_family;
  if (!ceilingFamilies.has(fallback) || !floorFamilies.has(fallback)) {
    problems.push(`the fallback family ${pyStr(fallback)} needs a ceiling and a floor`);
  }
  for (const family of [...ceilingFamilies].filter((f) => floorFamilies.has(f)).sort()) {
    const ceiling = ceilings[family];
    const floor = floors[family];
    if (!(floor > ceiling)) {
      problems.push(
        `${family}: the proof floor (${floor}) must be above the dust ceiling (${ceiling}), or an amount called dust would vouch for its sender`
      );
    }
  }
  const evmCeiling = ceilings.evm;
  if (evmCeiling !== void 0 && settled > evmCeiling) {
    problems.push(
      `native_dust_settled_wei_max (${settled}) must not exceed the evm family ceiling (${evmCeiling}): a rollup moves the same coin for less`
    );
  }
  return problems;
}
function resolveSpamThresholds(overrides) {
  let tuned;
  try {
    tuned = spamThresholdsWithOverrides(SPAM_THRESHOLDS, overrides);
  } catch {
    return SPAM_THRESHOLDS;
  }
  if (tuned === SPAM_THRESHOLDS) return tuned;
  return validateSpamThresholds(tuned).length === 0 ? tuned : SPAM_THRESHOLDS;
}
function spamThresholdsOf(effective) {
  return resolveSpamThresholds(isRecord(effective) ? effective[SPAM_THRESHOLDS_KEY] : void 0);
}
function spamThresholdProblems(value, where = SPAM_THRESHOLDS_KEY) {
  if (value === void 0 || value === null) return [];
  if (!isRecord(value)) return [`${where} must be an object keyed by threshold name`];
  const problems = [];
  const scalars = new Set(SPAM_THRESHOLD_SCALARS);
  const tables = new Set(SPAM_THRESHOLD_FAMILY_TABLES);
  for (const [name, raw] of Object.entries(value)) {
    const path = `${where}.${name}`;
    if (scalars.has(name)) {
      const { problem } = scalarOverride(name, raw);
      if (problem) problems.push(`${where}.${problem}`);
    } else if (tables.has(name)) {
      if (!isRecord(raw)) {
        problems.push(`${path} must be an object keyed by chain family`);
        continue;
      }
      const known = SPAM_THRESHOLDS[name];
      for (const [family, amount] of Object.entries(raw)) {
        if (!Object.hasOwn(known, family)) {
          problems.push(`${path}.${family}: no such family (families: ${Object.keys(known).sort().join(", ")})`);
          continue;
        }
        const parsed = whole(amount);
        if (parsed === null || parsed <= 0n || parsed > MAX_RAW) {
          problems.push(`${path}.${family} must be a positive whole number of the family's base unit`);
        }
      }
    } else {
      problems.push(`${path}: not tunable (tunable: ${SPAM_THRESHOLD_TUNABLES.join(", ")})`);
    }
  }
  if (problems.length) return problems;
  return validateSpamThresholds(spamThresholdsWithOverrides(SPAM_THRESHOLDS, value)).map((p) => `${where}: ${p}`);
}

// src/dust/index.ts
var BARE_TRANSFER_PROTOCOLS = /* @__PURE__ */ new Set([
  "native_transfer",
  "erc20",
  "erc721",
  "erc1155"
]);
var FALLBACK_PROTOCOL_ID = "generic";
var NON_PROTOCOL_IDS = /* @__PURE__ */ new Set([
  "native_transfer",
  "erc20",
  "erc721",
  "erc1155",
  "erc4626",
  "generic"
]);
function claimedProtocols(row) {
  const primary = row.primaryProtocolId || "";
  const out = [];
  const seen = /* @__PURE__ */ new Set();
  for (const id of [primary, ...row.protocolIds ?? []]) {
    if (!id || seen.has(id)) continue;
    seen.add(id);
    if (id === FALLBACK_PROTOCOL_ID && id !== primary) continue;
    out.push(id);
  }
  return out;
}
function isBareTransfer(row) {
  const claimed = claimedProtocols(row);
  return claimed.length > 0 && claimed.every((id) => BARE_TRANSFER_PROTOCOLS.has(id));
}
var DEFAULT_DUST_USD = DUST_USD_FLOOR;
function pricedAboveDust(valueUsd, floor = DEFAULT_DUST_USD) {
  return typeof valueUsd === "number" && Number.isFinite(valueUsd) && valueUsd >= floor;
}
var DEFAULT_CAMPAIGN_MIN_RECIPIENTS = CAMPAIGN_MIN_RECIPIENTS;
function dustReason(row, options = {}) {
  const floor = options.usdFloor ?? DEFAULT_DUST_USD;
  if (options.protectedIds && row.canonicalTxId && options.protectedIds.has(row.canonicalTxId)) {
    return null;
  }
  const usd = typeof row.valueUsd === "number" && Number.isFinite(row.valueUsd) ? row.valueUsd : null;
  const sender = (row.fromAddress ?? "").toLowerCase();
  const fromDuster = Boolean(sender && options.dustSenders?.has(sender));
  if (usd === null && !fromDuster) return null;
  if (usd !== null && usd >= floor) return null;
  if (!isBareTransfer(row)) return null;
  if (fromDuster) {
    return "Bare transfer from an address seen sending these in bulk \u2014 the shape of a dusting campaign.";
  }
  return `Bare transfer worth ${usd === 0 ? "nothing" : `under $${floor}`} \u2014 the shape of a dusting transfer.`;
}
function withoutDust(rows, read, options = {}) {
  const kept = [];
  let filtered = 0;
  let reason = null;
  for (const row of rows) {
    const why = dustReason(read(row), options);
    if (why === null) {
      kept.push(row);
      continue;
    }
    filtered += 1;
    reason = reason ?? why;
  }
  return { kept, filtered, reason };
}
function deriveDustSenders(rows, read, options = {}) {
  const minRecipients = options.minRecipients ?? DEFAULT_CAMPAIGN_MIN_RECIPIENTS;
  const recipients = /* @__PURE__ */ new Map();
  for (const row of rows) {
    const candidate = read(row);
    const sender = (candidate.fromAddress ?? "").toLowerCase();
    const recipient = (candidate.toAddress ?? "").toLowerCase();
    if (!sender || !recipient || sender === recipient) continue;
    if (!isBareTransfer(candidate)) continue;
    const seen = recipients.get(sender) ?? /* @__PURE__ */ new Set();
    seen.add(recipient);
    recipients.set(sender, seen);
  }
  const out = /* @__PURE__ */ new Set();
  for (const [sender, seen] of recipients) {
    if (seen.size >= minRecipients) out.add(sender);
  }
  return out;
}

export { BARE_TRANSFER_PROTOCOLS, CAMPAIGN_MIN_RECIPIENTS, CAMPAIGN_MIN_SENDS, DEFAULT_CAMPAIGN_MIN_RECIPIENTS, DEFAULT_DUST_USD, DUST_USD_FLOOR, FALLBACK_NATIVE_PROOF_MIN, FALLBACK_PROTOCOL_ID, NATIVE_DUST_MAX_BY_FAMILY, NATIVE_PROOF_MIN_BY_FAMILY, NON_PROTOCOL_IDS, SPAM_FALLBACK_FAMILY, SPAM_THRESHOLDS, SPAM_THRESHOLDS_KEY, SPAM_THRESHOLDS_SNAPSHOT, SPAM_THRESHOLD_BOUNDS, SPAM_THRESHOLD_FAMILY_TABLES, SPAM_THRESHOLD_PLATFORM_FAMILY_TABLES, SPAM_THRESHOLD_SCALARS, SPAM_THRESHOLD_TUNABLES, claimedProtocols, deriveDustSenders, dustReason, nativeProofMinForFamily, pricedAboveDust, resolveSpamThresholds, spamThresholdProblems, spamThresholdsOf, withoutDust };
//# sourceMappingURL=chunk-RAO7SHWG.js.map
//# sourceMappingURL=chunk-RAO7SHWG.js.map